import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import { api, setToken } from './api.js';
import { resetTelemetry } from './telemetry.js';
import { Header, Footer, RefPill } from './components/Chrome.jsx';
import Landing from './screens/Landing.jsx';
import Report from './report/Report.jsx';
import Admin, { INITIAL_ADMIN_UI } from './admin/Admin.jsx';

/*
 * survey-core is most of the bundle, against ~150KB for everything else.
 * Loaded eagerly it would be paid for by the landing page, by every staff
 * sign-in, and by a completed respondent who only ever opens their report --
 * none of whom render a survey.
 *
 * Split out, it is fetched when a respondent actually starts the assessment,
 * which is also the moment they are least sensitive to a short wait.
 */
const SurveyScreen = lazy(() => import('./survey/SurveyScreen.jsx'));

/**
 * The shell: who is signed in, which view is showing, and the body-level
 * state the stylesheet keys off.
 *
 * No router. The vanilla app switched views rather than URLs, and copying
 * that keeps behaviour identical and avoids a dependency for four views.
 * `/admin` stays what it was — an entry point that changes the sign-in copy,
 * NOT a way in. The same access code is required and the SERVER decides the
 * role; nothing here grants anything.
 *
 * `document.body` carries two things the CSS needs and React does not own:
 * `survey-locked`, which pins the survey's progress bar so it cannot scroll
 * away, and `data-view`, which lets the report bound its height without
 * bounding every other view.
 */
export default function App() {
  const [view, setView] = useState('login');
  const [session, setSession] = useState(null);
  const [typedCode, setTypedCode] = useState('');
  const [survey, setSurvey] = useState(null);
  const [report, setReport] = useState(null);
  const [fatal, setFatal] = useState(null);
  // The survey's line in the header: autosave state, or a failed submit.
  const [surveyStatus, setSurveyStatus] = useState(null);
  // Admin tab, owner filter, sort and DII filters. Held here, not in Admin,
  // so a drill-through into a company's report and back restores them, as
  // the vanilla admin module's state did.
  const [adminUi, setAdminUi] = useState(INITIAL_ADMIN_UI);
  // Set while a view change is being applied FROM a popstate, so the effect
  // below does not push the entry the browser just popped.
  const poppingRef = useRef(false);
  // Set while the app moves history itself (leaving the survey on submit),
  // so popstate does not treat that as the respondent pressing Back.
  const rewindingRef = useRef(false);
  // The survey's handler for a history move between its sections.
  const surveyNavRef = useRef(null);
  // What popstate needs to decide whether an entry can still be shown. Read
  // from a listener registered once, so it cannot close over state.
  const liveRef = useRef(null);
  liveRef.current = { view, session, survey, report };

  const adminEntry = typeof window !== 'undefined'
    && window.location.pathname.replace(/\/+$/, '') === '/admin';

  // Mirrors the vanilla `show()`: body flags, then scroll to top. Without
  // the scroll reset, moving from a long survey page to the report lands
  // the reader halfway down it.
  useEffect(() => {
    document.body.classList.toggle('survey-locked', view === 'survey');
    document.body.dataset.view = `view-${view}`;
    window.scrollTo({ top: 0 });
    setSurveyStatus(null);
  }, [view]);

  /**
   * Browser Back returns to the previous SCREEN, rather than leaving the app.
   *
   * There is no router and the URL deliberately does not change: screens are
   * swapped in place, exactly as the vanilla client did. What the vanilla
   * client also did -- and what this adds -- is nothing at all, so Back left
   * the site entirely from a survey, a report or the admin view.
   *
   * So an entry is pushed per view transition and popstate maps it back. The
   * survey is the exception: it writes one entry per SECTION itself (see
   * SurveyScreen), so Back inside it steps back a section. The URL is left
   * alone on purpose: a real path such as /survey would invite a refresh or
   * a shared link, and neither can restore a session, since the token lives
   * in memory. That is a router's problem to solve, not this one's.
   *
   * Back to `login` signs out rather than showing the landing page over a
   * live session: answers are saved, and the respondent's code resumes them
   * exactly where they were.
   */
  useEffect(() => {
    if (poppingRef.current) {
      poppingRef.current = false;
      return;
    }
    if (view === 'survey') return;
    if (window.history.state?.fidmatView === view) return;
    window.history.pushState({ fidmatView: view }, '');
  }, [view]);

  /*
   * Sign-out clears the token as well as the screens. It did not, and an
   * entry left in history could bring a screen back after sign-out: Forward
   * from the landing screen reopened the admin view, fully populated,
   * because its requests still carried the staff token. The server-side
   * session is untouched (there is no sign-out endpoint); it simply has
   * nothing left on the client that can present it.
   */
  const signOut = useCallback(() => {
    resetTelemetry();
    setToken(null);
    setSession(null);
    setSurvey(null);
    setReport(null);
    setTypedCode('');
    setAdminUi(INITIAL_ADMIN_UI);
    setView('login');
  }, []);

  /**
   * The server has resolved a code to a role. Route on what it said, never
   * on anything the client decided.
   */
  /**
   * Show a report, with its sector comparison if the respondent consented.
   *
   * The benchmark is fetched BEFORE the view switches, as the vanilla
   * showReport did, so the report renders once, complete, rather than
   * rendering bare and then growing comparisons. Declined consent or too
   * few peers means the comparison is absent, not an error.
   */
  const openReport = useCallback(async (payload) => {
    let benchmark = null;
    if (payload.consented) {
      try {
        benchmark = await api('/api/benchmark');
      } catch {
        benchmark = null;
      }
    }
    setReport({ report: payload, benchmark });
    setView('report');
  }, []);

  /*
   * Submitting is one-way: the assessment is complete and scored. Before the
   * report is shown, history is taken back past every section entry to the
   * landing entry beneath them, and the report is pushed from there. That
   * discards the section entries outright, so Back from a just-submitted
   * report signs out as it always has, and Forward has no survey to return
   * to. The popstate this causes is the app's own and is ignored.
   */
  const leaveSurveyHistory = useCallback(() => new Promise((resolve) => {
    const state = window.history.state;
    if (state?.fidmatView !== 'survey') return resolve();
    rewindingRef.current = true;
    let timer;
    const done = () => {
      window.removeEventListener('popstate', done);
      clearTimeout(timer);
      rewindingRef.current = false;
      resolve();
    };
    window.addEventListener('popstate', done);
    // Belt and braces: a popstate that never arrives must not hang the
    // respondent on "Scoring…".
    timer = setTimeout(done, 1000);
    window.history.go(-((state.section ?? 0) + 1));
  }), []);

  useEffect(() => {
    window.history.replaceState({ fidmatView: 'login' }, '');
    const onPop = (event) => {
      if (rewindingRef.current) return;
      const live = liveRef.current;
      const entry = event.state ?? {};
      const target = entry.fidmatView ?? 'login';

      // Between sections: the survey moves itself, through its own
      // section-change code, so section timings are recorded as for a button.
      if (target === 'survey' && live.view === 'survey' && surveyNavRef.current) {
        surveyNavRef.current(entry.section ?? 0);
        return;
      }

      // An entry is only shown if what it needs is still here. One left
      // over from a session that has ended (Forward after a sign-out) gets
      // the landing screen and is rewritten as a landing entry, rather than
      // an empty survey or a staff view with no staff member.
      const showable = target === 'survey' ? Boolean(live.session && live.survey)
        : target === 'report' ? Boolean(live.session && live.report)
          : target === 'admin' ? live.session?.kind === 'owner'
            : false;

      if (!showable) {
        if (target !== 'login') window.history.replaceState({ fidmatView: 'login' }, '');
        // Back to the landing screen ends the session rather than showing
        // it over a live one. Nothing is lost: answers are saved as they are
        // given, and the respondent's own code resumes them where they
        // stopped. Done HERE, not in an effect on `view`: sign-in sets the
        // session before it switches the view, so an effect would see
        // "login with a session" during that window and sign the respondent
        // straight back out.
        if (live.view !== 'login') poppingRef.current = true;
        signOut();
        return;
      }
      if (target !== live.view) {
        poppingRef.current = true;
        setView(target);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [signOut]);

  const onSurveySubmitted = useCallback(async (payload) => {
    await leaveSurveyHistory();
    await openReport(payload);
    // The survey is finished with; nothing may navigate back into it.
    setSurvey(null);
  }, [leaveSurveyHistory, openReport]);

  /**
   * Staff drill-through: open one company's report from an admin view.
   *
   * Navigation only. The server re-checks the staff session and the caller's
   * scope on this request; nothing here reaches a report that did not
   * already exist for them. Failure is reported with a blocking alert, as
   * the vanilla client did.
   */
  const openCompanyAsStaff = useCallback(async (company) => {
    try {
      const payload = await api(`/api/admin/report?companyId=${encodeURIComponent(company.companyId)}`);
      setReport({ report: payload.report, benchmark: payload.benchmark, staff: true });
      setView('report');
    } catch (error) {
      window.alert(error.status === 409
        ? 'That assessment is not complete yet, so it has no report.'
        : `Could not open that report: ${error.message}`);
    }
  }, []);

  const onSignedIn = useCallback(async (payload, code) => {
    setSession(payload);
    setTypedCode(code);

    if (payload.kind === 'owner') {
      setView('admin');
      return;
    }

    try {
      // A completed assessment goes straight to its report; anyone else goes
      // to the survey, resumed at the first unanswered required question.
      if (payload.hasResult) {
        await openReport(await api('/api/report'));
        return;
      }
      setSurvey(await api('/api/survey'));
      setView('survey');
    } catch (error) {
      setFatal(error.message);
    }
  }, [openReport]);

  const headerRight = (() => {
    if (view === 'admin' && session?.owner) {
      return <RefPill>{`${session.owner.name} · ${session.owner.role === 'admin' ? 'Admin' : 'Owner'}`}</RefPill>;
    }
    if (view === 'report' && report?.staff && session?.owner) {
      return <RefPill>{`${session.owner.name} · viewing a company`}</RefPill>;
    }
    if (view === 'report' && typedCode) return <RefPill>{typedCode}</RefPill>;
    if (view === 'survey' && surveyStatus) return <span className="saved-note">{surveyStatus}</span>;
    return null;
  })();

  return (
    <>
      <Header right={headerRight} />

      <main>
        {fatal && (
          <section className="view">
            <div className="landing-main">
              <div className="landing-col">
                <p className="login-error" role="alert">{fatal}</p>
                <button type="button" className="btn btn-primary" onClick={signOut}>Start again</button>
              </div>
            </div>
          </section>
        )}

        {!fatal && view === 'login' && <Landing onSignedIn={onSignedIn} adminEntry={adminEntry} />}

        {!fatal && view === 'survey' && survey && (
          <Suspense fallback={<section id="view-survey" className="view"><div className="survey-scroll"><div className="survey-container"><p className="map-loading">Loading your assessment…</p></div></div></section>}>
            <SurveyScreen
              definition={survey}
              answers={session?.answers ?? {}}
              onSubmitted={onSurveySubmitted}
              onStatus={setSurveyStatus}
              historyNav={surveyNavRef}
            />
          </Suspense>
        )}

        {!fatal && view === 'report' && report && (
          <section id="view-report" className="view">
            <div className="report-main">
              <Report report={report.report} benchmark={report.benchmark}>
                {report.staff && (
                  <StaffReturnBar
                    companyName={report.report.company.companyName}
                    role={session?.owner?.role}
                    onBack={() => setView('admin')}
                  />
                )}
              </Report>
            </div>
          </section>
        )}

        {!fatal && view === 'admin' && (
          <Admin owner={session?.owner} ui={adminUi} setUi={setAdminUi} onOpenCompany={openCompanyAsStaff} />
        )}
      </main>

      <Footer />
    </>
  );
}

/** Lets staff get back to the cohort without signing in again. */
function StaffReturnBar({ companyName, role, onBack }) {
  return (
    <div className="staff-return">
      <button type="button" className="btn btn-tertiary" onClick={onBack}>← Back to companies</button>
      <span className="staff-return-note">{`Viewing ${companyName} as ${role === 'admin' ? 'an admin' : 'their owner'}`}</span>
    </div>
  );
}
